import { type BillingInterval, Currency } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { toast } from '@utima/ui';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/Alert';
import { invalidateCurrentOrganization } from '@/services/invalidateCurrentOrganization';
import { trpc } from '@/services/trpc';

import { AssignPlanCard, type AssignPlanFormValues } from './AssignPlanCard';
import { BalanceAndTopUpCard } from './BalanceAndTopUpCard';
import { CurrentSubscriptionCard } from './CurrentSubscriptionCard';
import {
  AssignResultBanner,
  type AssignResultView,
} from './DunningAndResultCards';
import { InvoicesCard } from './InvoicesCard';
import { SuperAdminControlsCard } from './SuperAdminControlsCard';

/**
 * Capability object driving which plan-management actions are available. The
 * same view is mounted at two routes: SuperAdmin (full caps) and partner
 * billing (assign + read invoices + edit billing info; no destructive ops).
 * The capability object only keeps the UI honest — the TRPC procedures are the
 * real security boundary.
 */
export type PlanManagementCapabilities = {
  /** Show + edit the Stripe customer id (SuperAdmin only). */
  canLinkStripe: boolean;
  /** Mark paid / void / void & reissue / refresh link (SuperAdmin only). */
  canManageInvoices: boolean;
  /** Force-cancel subscription + dunning actions (SuperAdmin only). */
  canForceCancel: boolean;
};

export type PlanManagementViewProps = {
  organizationId: string;
  capabilities: PlanManagementCapabilities;
};

export function PlanManagementView({
  organizationId,
  capabilities,
}: PlanManagementViewProps) {
  const { t } = useLingui();
  const utils = trpc.useUtils();

  const [organizationData] = trpc.organizations.findOne.useSuspenseQuery({
    id: organizationId,
  });

  const orgCurrency = organizationData.currency ?? Currency.CZK;

  const [assignResult, setAssignResult] = useState<AssignResultView | null>(
    null,
  );

  // Net terms + DUZP are SuperAdmin-only invoice settings, owned here and edited
  // in the SuperAdmin controls card. The assign card consumes them to build the
  // payload; partners never see the inputs and keep these defaults.
  const [daysUntilDue, setDaysUntilDue] = useState(14);
  const [duzpDate, setDuzpDate] = useState('');

  const { data: hasUsers, isLoading: isLoadingHasUsers } =
    trpc.organizations.hasUsers.useQuery(
      { id: organizationId },
      { enabled: capabilities.canLinkStripe },
    );

  const { data: billingInfo } = trpc.billingInfo.getBillingInfo.useQuery({
    organizationId,
  });

  const { data: activeSubscription, isLoading: isLoadingSubscription } =
    trpc.subscriptions.getActive.useQuery({ organizationId });

  const { data: planCatalog } = trpc.subscriptions.getPlanCatalog.useQuery({
    currency: orgCurrency,
    organizationId,
  });

  const { data: subscriptionItems } =
    trpc.subscriptions.listSubscriptionItems.useQuery({ organizationId });

  const {
    data: invoices,
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = trpc.subscriptions.listInvoices.useQuery({ organizationId });

  const invalidateBillingQueries = () => {
    refetchInvoices();
    utils.subscriptions.getActive.invalidate({ organizationId });
    utils.subscriptions.listSubscriptionItems.invalidate({ organizationId });
    utils.organizations.findOne.invalidate({ id: organizationId });
    void invalidateCurrentOrganization(organizationId);
  };

  const assignPlanMutation = trpc.subscriptions.assignPlan.useMutation();

  const markInvoicePaidMutation =
    trpc.subscriptions.markInvoicePaid.useMutation({
      onSuccess: () => {
        toast.success(t`Invoice marked as paid.`);
        invalidateBillingQueries();
      },
      onError: error => {
        toast.error(t`Failed to mark invoice as paid.`, {
          description: error.message,
        });
      },
    });

  const voidInvoiceMutation = trpc.subscriptions.voidInvoice.useMutation({
    onSuccess: () => {
      toast.success(t`Invoice voided.`);
      invalidateBillingQueries();
    },
    onError: error => {
      toast.error(t`Failed to void invoice.`, { description: error.message });
    },
  });

  const voidAndReissueMutation =
    trpc.subscriptions.voidAndReissueInvoice.useMutation({
      onSuccess: result => {
        toast.success(t`Invoice voided and reissued.`, {
          description: result.sent
            ? t`The corrected invoice was emailed to the customer.`
            : undefined,
        });
        invalidateBillingQueries();
      },
      onError: error => {
        toast.error(t`Failed to reissue invoice.`, {
          description: error.message,
        });
      },
    });

  const refreshInvoiceUrlMutation =
    trpc.subscriptions.refreshInvoiceUrl.useMutation({
      onSuccess: () => {
        toast.success(t`Invoice link refreshed.`);
        refetchInvoices();
      },
      onError: error => {
        toast.error(t`Failed to refresh invoice link.`, {
          description: error.message,
        });
      },
    });

  const cancelSubscriptionMutation = trpc.subscriptions.cancel.useMutation({
    onSuccess: () => {
      toast.success(t`Subscription canceled.`);
      invalidateBillingQueries();
    },
    onError: error => {
      toast.error(t`Failed to cancel subscription.`, {
        description: error.message,
      });
    },
  });

  const cancelAtPeriodEndMutation = trpc.subscriptions.cancel.useMutation({
    onSuccess: () => {
      toast.success(t`Cancellation scheduled for the end of the period.`);
      invalidateBillingQueries();
    },
    onError: error => {
      toast.error(t`Failed to schedule cancellation.`, {
        description: error.message,
      });
    },
  });

  const resumeSubscriptionMutation =
    trpc.subscriptions.resumeSubscription.useMutation({
      onSuccess: () => {
        toast.success(t`Subscription resumed.`);
        invalidateBillingQueries();
      },
      onError: error => {
        toast.error(t`Failed to resume subscription.`, {
          description: error.message,
        });
      },
    });

  const hasOpenOverdueInvoice = useMemo(
    () => Boolean(invoices?.some(invoice => invoice.daysOverdue > 0)),
    [invoices],
  );

  // Without canLinkStripe we cannot probe hasUsers; assigning still requires a
  // Stripe customer, but the backend creates it from the client's own admin.
  const isAssignDisabled =
    (capabilities.canLinkStripe && (isLoadingHasUsers || !hasUsers)) ||
    assignPlanMutation.isPending;

  const runAssign = async (
    values: AssignPlanFormValues,
    replaceExisting: boolean,
  ) => {
    const result = await assignPlanMutation.mutateAsync({
      organizationId,
      ...values,
      replaceExisting,
    });

    setAssignResult({
      invoiceId: result.invoiceId,
      invoiceStatus: result.invoiceStatus,
      hostedInvoiceUrl: result.hostedInvoiceUrl,
      hostedInvoiceUrlExpiresAt: result.hostedInvoiceUrlExpiresAt,
      collectionMethodTarget: values.collectionMethodTarget,
      sent: result.sent,
    });

    toast.success(t`Plan assigned. Subscription is active immediately.`);
    invalidateBillingQueries();
  };

  const handleAssign = async (values: AssignPlanFormValues) => {
    const hasReplaceableSubscription = Boolean(
      activeSubscription?.stripeSubscriptionId,
    );

    if (
      hasReplaceableSubscription &&
      !window.confirm(
        t`This organization already has an active subscription. Cancel it and assign the new plan?`,
      )
    ) {
      return;
    }

    try {
      await runAssign(values, hasReplaceableSubscription);
    } catch {
      // Errors are surfaced by the global mutation error handler (queryClient).
    }
  };

  const handleMarkInvoicePaid = (invoiceId: string) => {
    if (!window.confirm(t`Mark this invoice as paid out of band?`)) {
      return;
    }

    markInvoicePaidMutation.mutate({ invoiceId });
  };

  const handleVoidInvoice = (invoiceId: string) => {
    if (
      !window.confirm(
        t`Void this invoice? This cannot be undone. The customer will no longer be able to pay it.`,
      )
    ) {
      return;
    }

    voidInvoiceMutation.mutate({ invoiceId });
  };

  const handleVoidAndReissue = (invoiceId: string, duzpDate: string) => {
    voidAndReissueMutation.mutate({ invoiceId, duzpDate, sendNow: true });
  };

  const handleRefreshLink = (invoiceId: string) => {
    refreshInvoiceUrlMutation.mutate({ invoiceId });
  };

  const handleForceCancel = () => {
    if (
      !window.confirm(
        t`Cancel this subscription immediately? Service stops now and the plan is removed. This cannot be undone.`,
      )
    ) {
      return;
    }

    cancelSubscriptionMutation.mutate({ organizationId, atPeriodEnd: false });
  };

  const handleCancelAtPeriodEnd = () => {
    if (
      !window.confirm(
        t`Schedule cancellation at the end of the current period? Service continues until then; you can resume before that date.`,
      )
    ) {
      return;
    }

    cancelAtPeriodEndMutation.mutate({ organizationId, atPeriodEnd: true });
  };

  const handleResume = () => {
    resumeSubscriptionMutation.mutate({ organizationId });
  };

  const cancelsAtPeriodEnd = Boolean(activeSubscription?.cancelsAtPeriodEnd);

  const pendingInvoiceId = voidInvoiceMutation.isPending
    ? voidInvoiceMutation.variables?.invoiceId
    : voidAndReissueMutation.isPending
      ? voidAndReissueMutation.variables?.invoiceId
      : refreshInvoiceUrlMutation.isPending
        ? refreshInvoiceUrlMutation.variables?.invoiceId
        : undefined;

  const canForceCancel =
    capabilities.canForceCancel &&
    !!activeSubscription?.tier &&
    activeSubscription.tier !== 'Free';

  const metadataTarget = activeSubscription?.metadata?.collectionMethodTarget;
  const currentPaymentMethod =
    assignResult?.collectionMethodTarget ??
    (metadataTarget === 'card' || metadataTarget === 'bank_transfer'
      ? metadataTarget
      : null);

  return (
    <>
      {capabilities.canLinkStripe &&
        !isLoadingHasUsers &&
        hasUsers === false &&
        !organizationData.stripeCustomerId && (
          <Alert
            variant='warning'
            title={<Trans>Organization has no users</Trans>}
            className='mb-6'
            description={
              <Trans>
                Assigning a plan requires a Stripe customer. Add an
                administrator to this organization first.
              </Trans>
            }
          />
        )}

      <div className='mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <CurrentSubscriptionCard
          tierName={
            activeSubscription?.tier && activeSubscription.tier !== 'Free'
              ? activeSubscription.tier
              : undefined
          }
          status={activeSubscription?.status}
          billingInterval={
            (activeSubscription?.billingInterval as BillingInterval) ?? null
          }
          periodStart={activeSubscription?.startDate ?? null}
          periodEnd={activeSubscription?.endDate ?? null}
          paymentMethod={currentPaymentMethod}
          items={subscriptionItems ?? []}
          isLoading={isLoadingSubscription}
          cancelsAtPeriodEnd={cancelsAtPeriodEnd}
          onCancelAtPeriodEnd={handleCancelAtPeriodEnd}
          onResume={handleResume}
          isCancelActionPending={
            cancelAtPeriodEndMutation.isPending ||
            resumeSubscriptionMutation.isPending
          }
        />

        <BalanceAndTopUpCard
          organizationId={organizationId}
          organization={organizationData}
          subscription={activeSubscription ?? null}
          canManualCredit={capabilities.canManageInvoices}
          onUpdated={invalidateBillingQueries}
        />
      </div>

      <AssignPlanCard
        organizationId={organizationId}
        orgCurrency={orgCurrency}
        catalog={planCatalog}
        billingInfo={billingInfo}
        daysUntilDue={daysUntilDue}
        duzpDate={duzpDate}
        disabled={isAssignDisabled}
        isAssigning={assignPlanMutation.isPending}
        onAssign={handleAssign}
      />

      <AssignResultBanner
        result={assignResult}
        onDismiss={() => setAssignResult(null)}
      />

      <InvoicesCard
        invoices={invoices}
        isLoading={isLoadingInvoices}
        canManageInvoices={capabilities.canManageInvoices}
        onMarkPaid={
          capabilities.canManageInvoices ? handleMarkInvoicePaid : undefined
        }
        isMarkingPaid={markInvoicePaidMutation.isPending}
        markingInvoiceId={markInvoicePaidMutation.variables?.invoiceId}
        onVoid={capabilities.canManageInvoices ? handleVoidInvoice : undefined}
        onVoidAndReissue={
          capabilities.canManageInvoices ? handleVoidAndReissue : undefined
        }
        onRefreshLink={handleRefreshLink}
        pendingInvoiceId={pendingInvoiceId}
      />

      {capabilities.canManageInvoices && (
        <SuperAdminControlsCard
          organizationId={organizationId}
          canLinkStripe={capabilities.canLinkStripe}
          stripeCustomerId={organizationData.stripeCustomerId ?? null}
          onStripeUpdate={() => {
            utils.organizations.findOne.invalidate({ id: organizationId });
            void invalidateCurrentOrganization(organizationId);
          }}
          daysUntilDue={daysUntilDue}
          onDaysUntilDueChange={setDaysUntilDue}
          duzpDate={duzpDate}
          onDuzpDateChange={setDuzpDate}
          hasOpenOverdueInvoice={hasOpenOverdueInvoice}
          canForceCancel={canForceCancel}
          onForceCancel={handleForceCancel}
          isCancelling={cancelSubscriptionMutation.isPending}
          disabled={assignPlanMutation.isPending}
        />
      )}
    </>
  );
}
